/*
 * Seeded randomness for the organic motion system.
 *
 * Pure: no DOM, no React, no wall clock. Every picture the library draws is a
 * function of a seed, so the same seed gives the same colony in tests, in a
 * still frame and in a live run.
 *
 * Exports
 *   mulberry32(seed)  -> () => number in [0, 1)
 *   hashString(str)   -> uint32 (FNV-1a, 32 bit)
 *   makeRng(seed)     -> { next, range(a, b), int(a, b) in [a, b), normal(),
 *                          chance(p), pick(arr), fork(label) }
 *
 * fork(label) derives a new stream from the ORIGINAL seed and the label, never
 * from the parent's current position. Drawing from one fork therefore never
 * shifts another, and adding a fork later never perturbs an existing stream.
 */

export function mulberry32(seed) {
  let a = seed >>> 0;
  return function next() {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function hashString(str) {
  let h = 0x811c9dc5;
  const s = String(str);
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function makeRng(seed) {
  const base = seed >>> 0;
  const next = mulberry32(base);
  return {
    next,
    range: (a, b) => a + (b - a) * next(),
    int: (a, b) => a + Math.floor((b - a) * next()),
    // Irwin-Hall of three uniforms: mean 0, standard deviation 1, bounded at
    // +-3, which is what a meander wants -- no rare enormous kink.
    normal: () => (next() + next() + next() - 1.5) * 2,
    chance: (p) => next() < p,
    pick: (arr) => arr[Math.floor(next() * arr.length)],
    fork: (label) => makeRng((base ^ hashString(label)) >>> 0),
  };
}
